(function () {
  'use strict';

  const GROUP_SELECTOR   = '#header-group';
  const COMPONENT_SEL    = '#header-component';
  const SECTION_ATTR     = 'data-header-theme';
  const CLASS_LIGHT      = 'gi-header--light';
  const CLASS_DARK       = 'gi-header--dark';
  const CLASS_SCROLLED   = 'gi-header--scrolled';
  const CLASS_TOP        = 'gi-header--top';
  const SCROLL_THRESHOLD = 80;

  /* 'light' means "this section has a LIGHT ground", which puts the header
     into its limestone treatment. It is the safe default: a template with
     no themed section is almost always a light content page, and guessing
     'dark' there paints ivory type onto limestone. */
  const DEFAULT_THEME = 'light';

  /* null, not DEFAULT_THEME. Seeding this with a real theme made the first
     applyTheme() call a no-op via the equality guard, so neither state
     class was added at load. */
  let currentTheme = null;
  let isScrolled   = false;
  let ticking      = false;

  /*
    Resolve the header from the DOM rather than by section id.

    This used to look for #shopify-section-header, which never matched
    anything. Shopify renders section-group sections as
    shopify-section-sections--<group>__<key>, so querySelector returned
    null and the guard below exited before a single line ran — every
    state class and both offsets were dead code. The classless CSS rules
    still applied, which is why the account control restyled correctly
    while the header never went transparent and the hero never moved.

    #header-component is rendered by sections/header.liquid itself, so
    walking up from it finds the wrapper whatever Shopify names it.
  */
  const headerComponent = document.querySelector(COMPONENT_SEL);
  const headerGroup = document.querySelector(GROUP_SELECTOR);
  if (!headerComponent) return;

  /*
    State classes go on <html>. The CSS then never needs to know
    Shopify's id scheme, and the classes survive section-group renames
    and theme updates.
  */
  const stateTarget = document.documentElement;

  function applyTheme(theme) {
    if (theme === currentTheme) return;
    currentTheme = theme;
    if (theme === 'dark') {
      stateTarget.classList.add(CLASS_LIGHT);
      stateTarget.classList.remove(CLASS_DARK);
    } else {
      stateTarget.classList.add(CLASS_DARK);
      stateTarget.classList.remove(CLASS_LIGHT);
    }
  }

  function applyScrollState(scrollY) {
    const atTop = scrollY < SCROLL_THRESHOLD;
    if (atTop && isScrolled) {
      isScrolled = false;
      stateTarget.classList.remove(CLASS_SCROLLED);
      stateTarget.classList.add(CLASS_TOP);
    } else if (!atTop && !isScrolled) {
      isScrolled = true;
      stateTarget.classList.add(CLASS_SCROLLED);
      stateTarget.classList.remove(CLASS_TOP);
    }
  }

  /*
    ── Sticky offsets ────────────────────────────────────────────────────
    Horizon publishes --header-height and --header-group-height onto
    document.body. Anything declared on :root cannot read them — custom
    properties inherit downward and body is a descendant of html — so a
    rule on :root silently fell back to 0px.

    These mirror the same measurements onto documentElement so both html
    and body can use them. Mirrored, never independently measured, so the
    values cannot drift from Horizon's.

    Two offsets, because they do different jobs:

      --gi-header-height         the header component, every row of it.
                                 Pulls the hero up so the film runs behind
                                 the floating header — but NOT behind the
                                 announcement bar, which is opaque, so
                                 sliding footage under it would only crop
                                 the image.

                                 Measured on the component, never on a
                                 row. This used to take the first
                                 .header__row and call it the header,
                                 which was the same thing right up until
                                 the navigation moved to a second row —
                                 then the hero was pulled up by the logo
                                 row alone and the menu row's worth of
                                 page ground stayed on show as a cream
                                 band under the announcement bar.

      --gi-sticky-header-offset  announcement bar + header. Used for
                                 scroll-padding and anchor scroll-margin,
                                 because both stick and an anchor must
                                 clear the pair.
  */
  function publishOffsets() {
    const headerHeight = Math.round(headerComponent.getBoundingClientRect().height);
    const groupHeight = headerGroup
      ? Math.round(headerGroup.getBoundingClientRect().height)
      : headerHeight;

    if (groupHeight > 0) {
      stateTarget.style.setProperty('--gi-sticky-header-offset', groupHeight + 'px');
    }
    if (headerHeight > 0) {
      stateTarget.style.setProperty('--gi-header-height', headerHeight + 'px');
    }
  }

  function watchOffsets() {
    publishOffsets();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', publishOffsets, { passive: true });
      return;
    }
    const ro = new ResizeObserver(publishOffsets);
    if (headerGroup) ro.observe(headerGroup);
    ro.observe(headerComponent);
  }

  const sectionObserver = new IntersectionObserver(
    function (entries) {
      const visible = entries
        .filter(e => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible.length > 0) {
        const theme = visible[0].target.getAttribute(SECTION_ATTR) || DEFAULT_THEME;
        applyTheme(theme);
      }
    },
    {
      root: null,
      rootMargin: '0px 0px -85% 0px',
      threshold: 0
    }
  );

  function observeSections() {
    const sections = document.querySelectorAll('[' + SECTION_ATTR + ']');
    if (sections.length === 0) {
      applyTheme(DEFAULT_THEME);
      return;
    }
    sections.forEach(section => sectionObserver.observe(section));
    applyTheme(sections[0].getAttribute(SECTION_ATTR) || DEFAULT_THEME);
  }

  function onScroll() {
    if (!ticking) {
      window.requestAnimationFrame(function () {
        applyScrollState(window.scrollY || window.pageYOffset);
        ticking = false;
      });
      ticking = true;
    }
  }

  function init() {
    const scrollY = window.scrollY || window.pageYOffset;
    if (scrollY < SCROLL_THRESHOLD) {
      stateTarget.classList.add(CLASS_TOP);
    } else {
      isScrolled = true;
      stateTarget.classList.add(CLASS_SCROLLED);
    }
    watchOffsets();
    observeSections();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* Run immediately. This script loads after the header markup, so the
     element already exists, and waiting for DOMContentLoaded only widened
     the window in which the header rendered without a state class. */
  init();

  /* Re-measure once everything has settled — web fonts and the logo image
     both change the header's height after first paint. */
  window.addEventListener('load', publishOffsets);

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function () {
      sectionObserver.disconnect();
      currentTheme = null;
      observeSections();
      publishOffsets();
    });
    document.addEventListener('shopify:section:unload', function () {
      sectionObserver.disconnect();
      currentTheme = null;
      observeSections();
      publishOffsets();
    });
  }

})();
